import { useTranslation } from 'react-i18next';
import { Textarea, Text } from '@kognitos/lattice';
import type { ComplexInputProps } from '../types';

/**
 * Complex input component for JSON-based data structures
 *
 * Renders a textarea for inputting complex types (lists, dictionaries, tables)
 * as JSON. Provides context-aware placeholders based on the type hint.
 *
 * @param props - Component props
 * @returns JSX element containing the textarea and help text
 */
export function ComplexInput({
  name,
  value,
  onChange,
  error,
  required,
  typeHint,
  placeholder,
  disabled,
}: ComplexInputProps) {
  const { t } = useTranslation('automation-inputs');

  /**
   * Generates an appropriate placeholder based on the type hint
   *
   * @returns Placeholder string with example JSON for the type
   */
  const getPlaceholder = () => {
    if (placeholder) return placeholder;

    switch (typeHint) {
      case 'list':
        return t('placeholders.list');
      case 'dictionary':
        return t('placeholders.dictionary');
      case 'table':
        return t('placeholders.table');
      default:
        return t('placeholders.json');
    }
  };

  return (
    <div>
      <Text level="small" color="muted" className="mb-2">
        {t('complex.enterJson', { field: name, typeHint })}
      </Text>
      <Textarea
        id={name}
        name={name}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={getPlaceholder()}
        required={required}
        aria-invalid={!!error}
        rows={6}
        className={`bg-background ${error ? 'border-destructive focus-visible:ring-destructive' : ''}`}
        disabled={disabled}
        data-testid={`input-${name}`}
      />
    </div>
  );
}
